import React from 'react';
import {formatPercent, formatUsdt} from '../utils/accountPerformance';

const formatCount = (value) => {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return '0';

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(number);
};

export default function AccountCard({ account, exchangeName = '—', valuation, onOpen }) {
  const labelText = account?.label ?? '—';
  const activeText = account?.is_active ? 'Active' : 'Inactive';

  const activeStrategies = Number(account?.strategies?.active ?? 0);
  const totalStrategies = Number(account?.strategies?.total ?? 0);
  const activeSubscriptions = Number(account?.subscriptions?.active ?? 0);
  const totalSubscriptions = Number(account?.subscriptions?.total ?? 0);
  const totalTrades = Number(account?.trades?.total ?? 0);
  const executedTrades = Number(account?.trades?.executed ?? 0);

  const hasActiveStrategy = activeStrategies > 0;
  const hasActiveSubscription = activeSubscriptions > 0;
  const valuationLoading = valuation === undefined;
  const initialValue = valuation?.initialValue ?? null;
  const currentValue = valuation?.currentValue ?? null;
  const changePercent = valuation?.changePercent ?? null;

  return (
    <article
      className="accounts-page__account-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.();
        }
      }}
      aria-label={`Open account ${labelText}`}
    >
      <div className="accounts-page__account-card-top">
        <div className="accounts-page__account-meta">
          <div className="accounts-page__account-number" title={labelText}>
            {labelText}
          </div>
        </div>

        <div className="accounts-page__account-badges">
          {hasActiveStrategy ? (
            <span className="accounts-page__badge accounts-page__badge--strategy">Active strategy</span>
          ) : null}
          {hasActiveSubscription ? (
            <span className="accounts-page__badge accounts-page__badge--subscription">Active subscription</span>
          ) : null}

          <span
            className={
              account?.is_active
                ? 'accounts-page__status accounts-page__status--active'
                : 'accounts-page__status accounts-page__status--inactive'
            }
          >
            {activeText}
          </span>
        </div>
      </div>

      <div className="accounts-page__account-kv">
        <div className="accounts-page__kv">
          <div className="accounts-page__kv-k">Exchange</div>
          <div className="accounts-page__kv-v" title={exchangeName}>
            {exchangeName}
          </div>
        </div>

        <div className="accounts-page__valuation-grid">
          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">Initial valuation</div>
            <div className="accounts-page__kv-v" title={valuationLoading ? 'Loading…' : formatUsdt(initialValue)}>
              {valuationLoading ? 'Loading…' : formatUsdt(initialValue)}
            </div>
          </div>

          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">Current valuation</div>
            <div className="accounts-page__kv-v" title={valuationLoading ? 'Loading…' : formatUsdt(currentValue)}>
              {valuationLoading ? 'Loading…' : formatUsdt(currentValue)}
            </div>
          </div>

          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">% change</div>
            <div
              className={`accounts-page__kv-v ${
                Number(changePercent) > 0
                  ? 'accounts-page__kv-v--positive'
                  : Number(changePercent) < 0
                    ? 'accounts-page__kv-v--negative'
                    : ''
              }`}
              title={valuationLoading ? 'Loading…' : formatPercent(changePercent)}
            >
              {valuationLoading ? 'Loading…' : formatPercent(changePercent)}
            </div>
          </div>
        </div>

        <div className="accounts-page__account-summary-grid">
          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">Strategy</div>
            <div
              className="accounts-page__kv-v"
              title={`Active / Total: ${formatCount(activeStrategies)} / ${formatCount(totalStrategies)}`}
            >
              {hasActiveStrategy ? `${formatCount(activeStrategies)} active` : 'none'}
            </div>
          </div>

          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">Subscription</div>
            <div
              className="accounts-page__kv-v"
              title={`Active / Total: ${formatCount(activeSubscriptions)} / ${formatCount(totalSubscriptions)}`}
            >
              {hasActiveSubscription ? `${formatCount(activeSubscriptions)} active` : 'none'}
            </div>
          </div>

          <div className="accounts-page__kv">
            <div className="accounts-page__kv-k">Trades</div>
            <div
              className="accounts-page__kv-v"
              title={`Total / Executed: ${formatCount(totalTrades)} / ${formatCount(executedTrades)}`}
            >
              {formatCount(totalTrades)}
            </div>
          </div>
        </div>
      </div>

      <div className="accounts-page__account-actions">
        <button
          type="button"
          className="accounts-page__btn accounts-page__btn--primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpen?.();
          }}
        >
          Details
        </button>
      </div>
    </article>
  );
}
